import { defineStore, storeToRefs } from "pinia";
import { useUiStore } from "@/stores/ui";
import { ListTerminalModel } from "@/api/terminals/types";
import { computed, ref } from "vue";

import api from "@/api/terminals";

export const ERROR_GET_TERMINALS =
  "There was a problem trying to load the terminals";

export const ERROR_GET_TANK_TECHNICAL_DETAILS =
  "There was a problem trying to load the tank technical details";

export const useTerminalsStore = defineStore("terminals", () => {
  const uiStore = useUiStore();

  const terminalsList = ref<ListTerminalModel[]>([]);
  const activeTerminal = ref<ListTerminalModel>();

  const terminalsByKey = computed((): { [key: string]: ListTerminalModel } => {
    return terminalsList.value.reduce((terminals, terminal) => {
      terminals[terminal.id] = { ...terminal };
      return terminals;
    }, {});
  });

  /**
   * Fetch the terminals list from the api
   */
  async function getTerminals() {
    uiStore.isLoading = true;

    try {
      const response = await api.getTerminals();
      terminalsList.value = response;
    } catch (error) {
      uiStore.addToast(
        "failed-load-terminals",
        ERROR_GET_TERMINALS,
        "error",
        error,
      );
    }
    uiStore.isLoading = false;
  }

  /**
   * Set the currently active terminal within the UI.
   */
  function setActiveTerminal(newActiveTerminal: ListTerminalModel) {
    activeTerminal.value = newActiveTerminal;

    const { lastTankId, lastReceiptPlanId, lastPipelineProduct } =
      storeToRefs(uiStore);
    lastTankId.value = null;
    lastReceiptPlanId.value = null;
    lastPipelineProduct.value = null;
    localStorage.setItem(
      "lastUsedTerminalId",
      activeTerminal.value.id.toString(),
    );
  }

  /**
   * Reset the active terminal, including clearing out the local storage
   */
  function resetActiveTerminal() {
    activeTerminal.value = undefined;
    localStorage.removeItem("lastUsedTerminalId");

    const { terminalOperationId: operationId } = storeToRefs(uiStore);
    operationId.value = null;
    localStorage.removeItem("lastUsedOperationId");
  }

  return {
    terminalsList,
    activeTerminal,

    terminalsByKey,
    getTerminals,
    setActiveTerminal,
    resetActiveTerminal,
  };
});
